import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CloudUpload, HardDrive } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DriveExplorer } from "@/components/graphisme/Drive";
import api from "@/lib/api";
import { toast } from "sonner";

export default function GraphismeDocuments() {
  const [status, setStatus] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    api.get("/graphisme/drive/status").then((r) => setStatus(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    const d = searchParams.get("drive");
    if (d === "connected") toast.success("Google Drive est maintenant connecté — les nouveaux fichiers y seront stockés.");
    if (d === "error") {
      const reasons = {
        denied: "la connexion a été refusée ou interrompue",
        state: "session de connexion expirée — réessayez",
        scopes: "l'accès à Google Drive n'a pas été accordé — à l'écran Google, cochez bien « Sélectionner tout » / l'accès Drive avant Continuer",
        drive: "Drive partagé introuvable — vérifiez que le compte connecté est « Gestionnaire de contenu » du Drive partagé DGA",
        refresh: "Google n'a pas fourni de jeton durable — réessayez",
        token: "échec de l'échange sécurisé avec Google — réessayez",
      };
      const r = searchParams.get("reason");
      toast.error(`La connexion à Google Drive a échoué : ${reasons[r] || "erreur inattendue"}.`);
    }
    if (d) setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  const [migrating, setMigrating] = useState(false);

  const connect = async () => {
    try {
      const r = await api.get("/graphisme/drive/oauth/connect");
      window.location.href = r.data.authorization_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Connexion impossible"); }
  };

  const migrate = async () => {
    setMigrating(true);
    try {
      const r = await api.post("/graphisme/drive/migrate-files");
      toast.success(`Migration terminée : ${r.data.migrated}/${r.data.total} fichier(s) basculé(s) sur Google Drive`);
      if (r.data.errors?.length) toast.warning(`${r.data.errors.length} fichier(s) en échec (voir console)`);
      const s = await api.get("/graphisme/drive/status");
      setStatus(s.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur pendant la migration"); }
    finally { setMigrating(false); }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-5" data-testid="graphisme-documents-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Documents</h1>
        <p className="text-sm text-neutral-500 mt-1">Espace de fichiers partagé avec chaque client — dossiers, images, vidéos, PDF et liens, synchronisés en temps réel avec l'espace client.</p>
      </div>
      {status && (
        <div className={`flex flex-wrap items-center gap-3 rounded-xl border px-4 py-3 text-sm ${status.storage === "gdrive" ? "bg-green-50 border-green-200 text-green-800" : "bg-amber-50 border-amber-200 text-amber-800"}`}
          data-testid="drive-storage-status">
          {status.storage === "gdrive" ? <CloudUpload className="w-4 h-4 shrink-0" /> : <HardDrive className="w-4 h-4 shrink-0" />}
          {status.storage === "gdrive" ? (
            <>
              <span className="flex-1">Stockage Google Drive actif{status.mode === "oauth" ? " (compte connecté)" : " (compte de service)"} — les nouveaux fichiers sont stockés sur le Drive partagé DGA.</span>
              {(status.legacy_files || 0) > 0 && (
                <Button size="sm" variant="outline" onClick={migrate} disabled={migrating} className="gap-1.5 border-green-300 text-green-800 hover:bg-green-100" data-testid="drive-migrate-btn">
                  <CloudUpload className="w-3.5 h-3.5" /> {migrating ? "Migration…" : `Migrer les ${status.legacy_files} fichier(s) existant(s) vers Drive`}
                </Button>
              )}
            </>
          ) : (
            <>
              <span className="flex-1">
                {status.oauth_ready
                  ? "Stockage local transitoire actif — connectez Google Drive pour stocker les fichiers sur le Drive partagé DGA."
                  : "Stockage local transitoire actif — en attente des identifiants Google (secrets GOOGLE_OAUTH_CLIENT_ID / GOOGLE_OAUTH_CLIENT_SECRET)."}
              </span>
              {status.oauth_ready && (
                <Button size="sm" onClick={connect} className="bg-neutral-900 hover:bg-neutral-800 text-white gap-1.5" data-testid="drive-connect-btn">
                  <CloudUpload className="w-3.5 h-3.5" /> Connecter Google Drive
                </Button>
              )}
            </>
          )}
        </div>
      )}
      <DriveExplorer side="agency" />
    </div>
  );
}
